<template>
  <div class="st" :class="{ 'is-busy': saving, 'is-dirty': dirty }">
    <div v-if="saving" class="st-progress" aria-hidden="true"><i /></div>
    <div class="st-main">
      <header class="st-top">
        <div>
          <p class="st-kicker">{{ kicker }}</p>
          <h1 class="st-title">{{ title }}</h1>
          <p v-if="sub" class="st-sub">{{ sub }}</p>
        </div>
      </header>
      <slot />
    </div>

    <transition name="st-overlay">
      <div v-if="leaveOpen" class="st-scrim" @click.self="stayOnPage">
        <div
          class="st-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="st-leave-title"
        >
          <button
            type="button"
            class="st-sheet-handle"
            aria-label="Stay on this page"
            @click="stayOnPage"
          >
            <i />
          </button>
          <p class="st-sheet-kicker">{{ kicker }}</p>
          <h2 id="st-leave-title">Unsaved changes</h2>
          <p class="st-sheet-lead is-warn">
            Leave this page without saving? Your edits will be lost.
          </p>
          <div class="st-sheet-actions">
            <button class="st-sheet-quiet" type="button" @click="stayOnPage">
              Stay
            </button>
            <button class="st-sheet-leave" type="button" @click="leavePage">
              Leave without saving
            </button>
          </div>
        </div>
      </div>
    </transition>

    <div class="st-dock" :class="{ 'is-dirty': dirty }" role="status">
      <div class="st-dock-inner">
        <p class="st-dock-status">
          {{ saving ? "Saving…" : dirty ? "Unsaved changes" : "Saved" }}
        </p>
        <div class="st-dock-actions">
          <button
            class="st-chip"
            type="button"
            :disabled="saving || !dirty"
            @click="$emit('discard')"
          >
            Discard
          </button>
          <button
            class="st-chip is-primary"
            :class="{ 'is-ready': dirty && !saving }"
            type="submit"
            :form="formId"
            :disabled="saving || !dirty"
          >
            {{ saving ? "Saving…" : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    kicker: { type: String, required: true },
    title: { type: String, required: true },
    sub: { type: String, default: "" },
    formId: { type: String, required: true },
    dirty: { type: Boolean, default: false },
    saving: { type: Boolean, default: false }
  },
  data() {
    return {
      leaveOpen: false,
      leaveNext: null
    };
  },
  created() {
    document.body.classList.add("st-surface");
    window.addEventListener("beforeunload", this.onBeforeUnload);
    window.addEventListener("keydown", this.onLeaveKey);
    this.removeGuard = this.$router.beforeEach((to, from, next) => {
      if (to.fullPath === from.fullPath) {
        next();
        return;
      }
      if (!this.dirty || this.saving) {
        next();
        return;
      }
      if (this.leaveOpen) {
        next(false);
        return;
      }
      this.leaveNext = next;
      this.leaveOpen = true;
    });
  },
  beforeDestroy() {
    document.body.classList.remove("st-surface");
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.removeEventListener("keydown", this.onLeaveKey);
    if (this.removeGuard) this.removeGuard();
    if (this.leaveNext) {
      this.leaveNext(false);
      this.leaveNext = null;
    }
  },
  methods: {
    stayOnPage() {
      if (this.leaveNext) this.leaveNext(false);
      this.leaveNext = null;
      this.leaveOpen = false;
    },
    leavePage() {
      const next = this.leaveNext;
      this.leaveNext = null;
      this.leaveOpen = false;
      this.$emit("allow-leave");
      if (next) next();
    },
    onLeaveKey(event) {
      if (event.key === "Escape" && this.leaveOpen) this.stayOnPage();
    },
    onBeforeUnload(event) {
      if (!this.dirty || this.saving) return;
      event.preventDefault();
      event.returnValue = "";
    }
  }
};
</script>
